import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMyVotes, getCollectionVoteInfo } from "../../services/learningApi";
import "./Vote.css";

const PAGE_LIMIT = 20;

const statusLabels = {
  active: "فعال",
  invalidated: "نامعتبر",
};

const MyVotes = () => {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [enriched, setEnriched] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadPage = useCallback(async (page) => {
    setLoading(true);
    setError("");
    try {
      const response = await getMyVotes({ page, limit: PAGE_LIMIT });
      const data = response?.data;
      const nextItems = data?.items || [];
      setItems(nextItems);
      setPagination(data?.pagination || null);

      // ارجحیت نمایش: نام و کاور فیلم به‌صورت موازی از endpoint کالکشن گرفته می‌شود
      if (nextItems.length > 0) {
        const results = await Promise.allSettled(
          nextItems.map((item) =>
            getCollectionVoteInfo({ collectionId: item.collectionId })
          )
        );
        const map = {};
        results.forEach((result, idx) => {
          const item = nextItems[idx];
          if (result.status === "fulfilled" && result.value?.data) {
            map[item.collectionId] = result.value.data;
          }
        });
        setEnriched(map);
      } else {
        setEnriched({});
      }
    } catch (err) {
      console.error("load my votes error:", err);
      setError(err?.message || "خطا در دریافت درخواست‌ها");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const handlePage = (page) => {
    if (!pagination) return;
    if (page < 1 || page > pagination.totalPages) return;
    loadPage(page);
  };

  const pageNumbers = () => {
    if (!pagination) return [];
    const current = pagination.page;
    const total = pagination.totalPages;
    const nums = new Set([1]);
    for (let p = current - 2; p <= current + 2; p++) {
      if (p >= 1 && p <= total) nums.add(p);
    }
    nums.add(total);
    return [...nums].sort((a, b) => a - b);
  };

  return (
    <>
      <h3 className="vote-section-title">
        <i className="fas fa-clipboard-list"></i>
        درخواست‌های من
      </h3>

      {loading && items.length === 0 && (
        <div className="vote-state">
          <div className="spinner"></div>
          <p>در حال دریافت درخواست‌های شما…</p>
        </div>
      )}

      {!loading && error && (
        <div className="vote-state">
          <i className="fas fa-triangle-exclamation vote-state-icon"></i>
          <p>{error}</p>
          <button
            className="vote-btn vote-btn-primary"
            style={{ marginTop: 16 }}
            onClick={() => loadPage(pagination?.page || 1)}
          >
            <i className="fas fa-rotate-right"></i>
            تلاش مجدد
          </button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="vote-state">
          <i className="fas fa-inbox vote-state-icon"></i>
          <p>هنوز درخواستی ثبت نکرده‌اید.</p>
          <p style={{ fontSize: "0.85rem", marginTop: 8 }}>
            از صفحه اصلی یک کالکشن دو زیرنویس پیدا کنید و درخواست استخراج
            لغات ثبت کنید.
          </p>
          <Link
            to="/vote"
            className="vote-btn vote-btn-primary"
            style={{ marginTop: 16 }}
          >
            <i className="fas fa-home"></i>
            بازگشت به خانه
          </Link>
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="vote-list">
            {items.map((item) => {
              const info = enriched[item.collectionId];
              const movieName =
                info?.movieName || `کالکشن ${item.collectionId}`;
              const coverUrl = info?.coverUrl;
              const isSeries = info?.videoType === "series";
              const date = new Date(item.createdAt).toLocaleDateString("fa-IR", {
                year: "numeric",
                month: "long",
                day: "numeric",
              });

              return (
                <Link
                  key={item.collectionId}
                  to={`/vote/collections/${encodeURIComponent(item.collectionId)}`}
                  className="vote-list-item"
                >
                  <div className="vote-list-cover">
                    {coverUrl ? (
                      <img src={coverUrl} alt="" loading="lazy" />
                    ) : (
                      <i className="fas fa-film"></i>
                    )}
                  </div>
                  <div className="vote-list-body">
                    <div className="vote-list-title">{movieName}</div>
                    <div className="vote-list-meta">
                      <span className="vote-chip teal small">
                        <i className="fas fa-hashtag"></i>
                        {item.collectionId}
                      </span>
                      {info && isSeries && (
                        <span className="vote-chip gray small">
                          <i className="fas fa-tv"></i>
                          فصل {info.season ?? "؟"} قسمت {info.episode ?? "؟"}
                        </span>
                      )}
                      <span className={`vote-status ${item.status}`}>
                        <i
                          className={`fas ${
                            item.status === "active"
                              ? "fa-circle-check"
                              : "fa-circle-xmark"
                          }`}
                        ></i>
                        {statusLabels[item.status] || item.status}
                      </span>
                    </div>
                    <div className="vote-list-meta" style={{ marginTop: 4 }}>
                      <span>
                        <i className="far fa-calendar"></i>
                        {date}
                      </span>
                      <span>
                        <i className="fas fa-align-right"></i>
                        <span dir="ltr">{item.subtitleUploadId}</span>
                      </span>
                    </div>
                  </div>
                  <div className="vote-list-votes">
                    <strong>{item.activeVotes}</strong>
                    <span>رأی فعال</span>
                  </div>
                  <i className="fas fa-chevron-left vote-list-arrow"></i>
                </Link>
              );
            })}
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="vote-pagination">
              <button
                className="vote-page-btn"
                disabled={!pagination.hasPrevPage}
                onClick={() => handlePage(pagination.page - 1)}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
              {pageNumbers().map((num, idx, arr) => (
                <React.Fragment key={num}>
                  {idx > 0 && arr[idx - 1] !== num - 1 && (
                    <span style={{ color: "var(--text-muted)" }}>…</span>
                  )}
                  <button
                    className={`vote-page-btn ${
                      num === pagination.page ? "current" : ""
                    }`}
                    onClick={() => handlePage(num)}
                  >
                    {num}
                  </button>
                </React.Fragment>
              ))}
              <button
                className="vote-page-btn"
                disabled={!pagination.hasNextPage}
                onClick={() => handlePage(pagination.page + 1)}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <div className="vote-page-info">
                صفحه {pagination.page} از {pagination.totalPages} · مجموع{" "}
                {pagination.total} درخواست
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default MyVotes;